import React, { useState, useContext, useEffect, useRef } from 'react';
import { PropsWithChildren } from 'react';
import agent from '../agent';
import types from '../types/services-api';
import ProfileContext from './ProfileContext';
import JobSearchContext from './JobSearchContext';
import ConversationsContext from './ConversationsContext';
import InterviewContext from './InterviewContext';
import { PurchaseContext } from './PurchaseContext';
import { GlobalContext } from './GlobalContext';
import { initializePushNotifications } from '../services/pushNotifications';
import { navigate } from 'gatsby';
import { getAuthToken, getUserToken } from '../utils/authentication';

export type UserType = types.UserEmployerViewType | types.StaffProfileViewType;

type SharedContextType = {
  defaultMapCenter: { lat: number; lng: number } | undefined;
  setDefaultMapCenter: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number } | undefined>
  >;
  updateUser: (updatedUserValues: {}) => void;
  fetchUser: (role: 'employer' | 'staff') => void;
  fetchError: string;
  resetUser: () => void;
  isLoading: boolean;
};
export interface StaffContextType extends SharedContextType {
  user: types.StaffProfileViewType;
}

export interface EmployerContextType extends SharedContextType {
  user: types.UserEmployerViewType;
  employerTechnologiesAssessment: {
    skill: string;
    score: number;
    category: string;
  }[];
}

export type UserContextType = StaffContextType | EmployerContextType;

export function isEmployerType(
  object: any
): object is types.UserEmployerViewType {
  return !!object && 'empType' in object;
}

export function isStaffType(object: any): object is types.StaffProfileViewType {
  return !!object && 'position' in object;
}

export const UserContext = React.createContext<
  | UserContextType
  | {
      user: null;
      isLoading: boolean;
      fetchUser: (role: 'employer' | 'staff') => void;
      fetchError: string;
      updateUser: (updatedUserValues: {}) => void;
      resetUser: () => void;
    }
>({
  user: null,
  isLoading: false,
  fetchUser: (role: 'employer' | 'staff') => {},
  fetchError: '',
  updateUser: (updatedUserValues: {}) => {},
  resetUser: () => {},
});

const UserContextWrapper = (props: PropsWithChildren) => {
  const { urlParams, userAuth } = useContext(GlobalContext);
  const { getCatalog } = useContext(PurchaseContext);
  const [user, setUser] = useState<UserType | null>(null);
  const [employerTechnologiesAssessment, setEmployerTechnologiesAssessment] =
    useState<{ skill: string; score: number; category: string }[]>([]);
  const [defaultMapCenter, setDefaultMapCenter] = useState<
    | {
        lat: number;
        lng: number;
      }
    | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const pushInitialized = useRef(false);
  const latestFetchIdRef = useRef(0);
  const latestCatalogDiscountCodeRef = useRef<string | undefined>(undefined);
  const latestPublicCatalogDiscountCodeRef = useRef<string | undefined>(
    undefined
  );
  const searchDiscountCode = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search);
    return (
      params.get('discount-code') || params.get('discountCode') || undefined
    );
  }, [urlParams.discountCode]);
  const resolvedDiscountCode = urlParams.discountCode ?? searchDiscountCode;

  // Keyed on identity fields rather than the userAuth object so a token
  // refresh for the same user doesn't refetch the profile.
  useEffect(() => {
    if (userAuth.isEmployer) {
      fetchUser('employer');
    } else if (userAuth.isStaff) {
      fetchUser('staff');
    } else if (!userAuth.isAuthenticated && !!user) {
      console.warn('[auth] resetUser', {
        authToken: !!getAuthToken(),
        userData: !!getUserToken(),
      });
      resetUser();
    }
  }, [userAuth.username, userAuth.isEmployer, userAuth.isStaff]);

  useEffect(() => {
    if (user && !pushInitialized.current) {
      pushInitialized.current = true;
      initializePushNotifications((path: string) => {
        navigate(path);
      });
    }
    if (!user) {
      pushInitialized.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!isEmployerType(user)) {
      latestCatalogDiscountCodeRef.current = undefined;
      return;
    }

    if (latestCatalogDiscountCodeRef.current !== urlParams.discountCode) {
      latestCatalogDiscountCodeRef.current = urlParams.discountCode;
      getCatalog({ role: 'employer', discountCode: urlParams.discountCode });
    }
  }, [user, urlParams.discountCode, getCatalog]);

  useEffect(() => {
    if (!!user) {
      latestPublicCatalogDiscountCodeRef.current = undefined;
      return;
    }

    if (!resolvedDiscountCode) {
      return;
    }

    if (latestPublicCatalogDiscountCodeRef.current !== resolvedDiscountCode) {
      latestPublicCatalogDiscountCodeRef.current = resolvedDiscountCode;
      getCatalog({ discountCode: resolvedDiscountCode });
    }
  }, [user, resolvedDiscountCode, getCatalog]);

  function fetchUser(role: 'employer' | 'staff') {
    const fetchId = latestFetchIdRef.current + 1;
    latestFetchIdRef.current = fetchId;

    setIsLoading(true);
    setFetchError('');

    if (role === 'employer') {
      fetchEmployerUser(fetchId);
      getCatalog({ role, discountCode: resolvedDiscountCode });
    } else if (role === 'staff') {
      fetchStaffUser(fetchId);
    }
  }

  function fetchEmployerUser(fetchId: number) {
    setIsLoading(true);
    agent.Employer.current()
      .then((res) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        setUser({
          ...res.data,
        });
        fetchEmployerTechnologies(res.data.technologies, fetchId);
      })
      .catch((err) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        setFetchError(err.message);
      })
      .finally(() => {
        if (latestFetchIdRef.current === fetchId) {
          setIsLoading(false);
        }
      });
  }

  function fetchEmployerTechnologies(skills: string[], fetchId: number) {
    agent.Lists.getEmployerSkills()
      .then((res) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        const updatedAssessment = res.data.map((obj) => {
          return {
            skill: obj.skill,
            score: skills.includes(obj.skill) ? 1 : 0,
            category: obj.category,
          };
        });
        setEmployerTechnologiesAssessment(updatedAssessment);
      })
      .catch((err) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        console.log(err.message);
        setEmployerTechnologiesAssessment([]);
      });
  }

  function fetchStaffUser(fetchId: number) {
    setIsLoading(true);
    agent.Staff.current()
      .then((res) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        setUser({
          ...res.data,
        });
        setDefaultMapCenter({
          lat: Number(res.data.lat),
          lng: Number(res.data.lng),
        });
      })
      .catch((err) => {
        if (latestFetchIdRef.current !== fetchId) {
          return;
        }

        setFetchError(err.message);
      })
      .finally(() => {
        if (latestFetchIdRef.current === fetchId) {
          setIsLoading(false);
        }
      });
  }

  function updateUser(updatedUserValues: {}) {
    if (!!user) {
      setUser({ ...user, ...updatedUserValues });
    }
  }

  function resetUser() {
    setUser(null);
  }

  //for DevTools to display context name
  UserContext.displayName = 'User Context';

  return (
    <UserContext.Provider
      value={{
        defaultMapCenter,
        setDefaultMapCenter,
        user,
        fetchUser,
        fetchError,
        resetUser,
        updateUser,
        isLoading,
        employerTechnologiesAssessment,
      }}
    >
      {(isEmployerType(user) ||
        (isStaffType(user) && user.position !== null)) &&
      !isLoading ? (
        <ProfileContext>
          <JobSearchContext>
            <InterviewContext>
              <ConversationsContext>{props.children} </ConversationsContext>
            </InterviewContext>
          </JobSearchContext>
        </ProfileContext>
      ) : (
        props.children
      )}
    </UserContext.Provider>
  );
};

export default UserContextWrapper;
